import { useSearchParams } from 'react-router-dom';
import { useState } from 'react';

/**
 * The page a patroller lands on from the setup link their site admin sends
 * (see admin/Settings.tsx). Opened on the phone itself, it walks them through
 * install → point the app at this site → sign in.
 *
 * Public and unauthenticated by design: a patroller has no portal login, and
 * the site ID it carries is not a credential — signing in still needs their
 * own username and password.
 *
 * The deep link uses the custom `patrolit://` scheme from app.json rather than
 * an https App Link, which would need assetlinks.json hosting and Google's
 * verification round-trip. The trade-off is that nothing happens if the app
 * is not installed yet, so step 1 is the install and the manual site ID is
 * always shown as a fallback.
 */
const APK_URL = '/app/patrolit.apk';

function deepLink(siteId: string): string {
  return `patrolit:///(auth)/onboarding?site=${encodeURIComponent(siteId)}`;
}

export default function AppSetup() {
  const [params] = useSearchParams();
  const siteId = params.get('site') ?? '';
  const [copied, setCopied] = useState(false);

  function copyId() {
    navigator.clipboard.writeText(siteId).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  if (!siteId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-xl border border-gray-200 p-6 max-w-md text-center">
          <h1 className="text-lg font-semibold text-gray-900 mb-2">Setup link incomplete</h1>
          <p className="text-sm text-gray-500">
            This link is missing its site. Ask your site administrator to resend the
            setup link from the portal's Settings page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-10">
      <div className="max-w-md mx-auto space-y-4">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">PatrolIt</h1>
          <p className="text-sm text-gray-500 mt-1">Set up the patroller app on this phone</p>
        </div>

        <section className="bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="font-semibold text-gray-800 mb-1">1. Install the app</h2>
          <p className="text-xs text-gray-500 mb-4">
            Android will ask permission to install from this source — that is normal for an
            app installed outside the Play Store. Skip this step if it is already installed.
          </p>
          <a
            href={APK_URL}
            className="block w-full text-center bg-brand-600 text-white rounded-lg py-3 text-sm font-semibold hover:bg-brand-700 transition-colors"
          >
            Download PatrolIt
          </a>
        </section>

        <section className="bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="font-semibold text-gray-800 mb-1">2. Point it at your site</h2>
          <p className="text-xs text-gray-500 mb-4">
            Opens the app and fills in your site. You will be asked to confirm the site name
            before anything is saved.
          </p>
          <a
            href={deepLink(siteId)}
            className="block w-full text-center bg-brand-600 text-white rounded-lg py-3 text-sm font-semibold hover:bg-brand-700 transition-colors"
          >
            Open in the PatrolIt app
          </a>

          <div className="mt-5 pt-5 border-t border-gray-100">
            <p className="text-xs text-gray-500 mb-2">
              If that button does nothing, the app is not installed yet. Install it first,
              then open it and enter this site ID by hand:
            </p>
            <div className="flex items-center gap-2">
              <input
                readOnly
                value={siteId}
                onFocus={(e) => e.target.select()}
                className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-xs font-mono bg-gray-50 text-gray-700 select-all"
              />
              <button
                onClick={copyId}
                className="px-3 py-2 rounded-lg border border-gray-300 text-xs text-gray-600 hover:bg-gray-50 transition-colors whitespace-nowrap"
              >
                {copied ? '✓' : 'Copy'}
              </button>
            </div>
          </div>
        </section>

        <section className="bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="font-semibold text-gray-800 mb-1">3. Sign in</h2>
          <p className="text-xs text-gray-500">
            Pick your name in the app and enter your own password. Your site administrator
            sets these up — this link does not give anyone access on its own.
          </p>
        </section>
      </div>
    </div>
  );
}
